import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import useAuthStore from '@/stores/authStore';

// Pages
import LoginPage from '@/pages/LoginPage';
import ChangePasswordPage from '@/pages/ChangePasswordPage';
import PrivacyPage from '@/pages/PrivacyPage';
import AdminLayout from '@/layouts/AdminLayout';
import DashboardPage from '@/pages/admin/DashboardPage';
import TournamentSetupPage from '@/pages/admin/TournamentSetupPage';
import TeamsPage from '@/pages/admin/TeamsPage';
import PlayersPage from '@/pages/admin/PlayersPage';
import MatchesPage from '@/pages/admin/MatchesPage';
import ApiKeysPage from '@/pages/admin/ApiKeysPage';
import AuditPage from '@/pages/admin/AuditPage';
import SponsorsPage from '@/pages/admin/SponsorsPage';
import TournamentsPage from '@/pages/admin/TournamentsPage';
import ScoreboardPage from '@/pages/admin/ScoreboardPage';
import AuctionPage from '@/pages/admin/AuctionPage';
import PlayerCategoriesPage from '@/pages/admin/PlayerCategoriesPage';
import TeamDetailPage from '@/pages/admin/TeamDetailPage';
import TournamentDetailPage from '@/pages/admin/TournamentDetailPage';
import UsersPage from '@/pages/admin/UsersPage';
import PlayerProfilePage from '@/pages/admin/PlayerProfilePage';
import ScorersPage from '@/pages/admin/ScorersPage';
import MemberProfilePage from '@/pages/admin/MemberProfilePage';
import PlayerRankingsPage from '@/pages/admin/PlayerRankingsPage';
import PlayerScoresPage from '@/pages/admin/PlayerScoresPage';
import { injectGlobalPrintCSS } from '@/lib/exportUtils';
import TournamentReportPage from '@/pages/admin/TournamentReportPage';
import PublicScoreboardPage from '@/pages/PublicScoreboardPage';
import PublicPlayerCardPage from '@/pages/PublicPlayerCardPage';
import BroadcastPage from '@/pages/BroadcastPage';
import ScorerLoginPage from '@/pages/ScorerLoginPage';
import ScorerLayout from '@/layouts/ScorerLayout';
import ScorerMatchListPage from '@/pages/scorer/ScorerMatchListPage';
import ScorerEntryPage from '@/pages/scorer/ScorerEntryPage';
import ScorerEncounterListPage from '@/pages/scorer/ScorerEncounterListPage';
import ScorerEncounterPage from '@/pages/scorer/ScorerEncounterPage';
import ScorerSportPage from '@/pages/scorer/ScorerSportPage';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore();
  const [checking, setChecking] = useState(true);
  const [hasToken, setHasToken] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('btccp_access_token');
    setHasToken(!!token);
    setChecking(false);
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-[#06080d] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#f5c518]" />
      </div>
    );
  }

  if (hasToken && isLoading) {
    return (
      <div className="min-h-screen bg-[#06080d] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#f5c518]" />
      </div>
    );
  }

  if (!hasToken) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function App() {
  const { fetchMe, isAuthenticated, user } = useAuthStore();

  useEffect(() => {
    // Always call fetchMe on mount when token exists
    const token = localStorage.getItem('btccp_access_token');
    if (token) fetchMe();
    // Inject global print CSS with attribution footer
    injectGlobalPrintCSS();
  }, []); // only on mount

  return (
    <div className="min-h-screen bg-[#06080d] text-[#f0f2f7]">
      <BrowserRouter>
        <Routes>
          {/* Public routes — no auth needed */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/scorer-login" element={<ScorerLoginPage />} />
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/public/tournaments/:tournamentId" element={<PublicScoreboardPage />} />
          <Route path="/player/:playerId" element={<PublicPlayerCardPage />} />
          <Route path="/tv/:tournamentId" element={<BroadcastPage />} />
          <Route path="/tv" element={<BroadcastPage />} />

          {/* Admin routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardPage />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="tournaments" element={<TournamentsPage />} />
            <Route path="tournaments/:tournamentId" element={<TournamentDetailPage />} />
            <Route path="tournament-setup" element={<TournamentSetupPage />} />
            <Route path="teams" element={<TeamsPage />} />
            <Route path="players" element={<PlayersPage />} />
            <Route path="players/:playerId" element={<PlayerProfilePage />} />
            <Route path="matches" element={<MatchesPage />} />
            <Route path="sponsors" element={<SponsorsPage />} />
            <Route path="auction" element={<AuctionPage />} />
            <Route path="scoreboard" element={<ScoreboardPage />} />
            <Route path="player-categories" element={<PlayerCategoriesPage />} />
            <Route path="teams/:teamId" element={<TeamDetailPage />} />
            <Route path="api-keys" element={<ApiKeysPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="scorers" element={<ScorersPage />} />
            <Route path="profile" element={<MemberProfilePage />} />
            <Route path="rankings" element={<PlayerRankingsPage />} />
            <Route path="player-scores" element={<PlayerScoresPage />} />
            <Route path="tournaments/:tid/report" element={<TournamentReportPage />} />
          </Route>

          {/* Scorer routes */}
          <Route
            path="/score"
            element={
              <ProtectedRoute>
                <ScorerLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<ScorerEncounterListPage />} />
            <Route path="enc/:encId" element={<ScorerEncounterPage />} />
            <Route path="enc/:encId/sport/:sportCode" element={<ScorerSportPage />} />
            <Route path="legacy" element={<ScorerMatchListPage />} />
            <Route path=":matchId" element={<ScorerEntryPage />} />
          </Route>

          {/* Redirect root to admin */}
          <Route path="/" element={<Navigate to="/admin" replace />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
